import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Chip } from "@mui/material";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminConversations,
  setActiveConversation,
  clearUnreadForConversation,
} from "../../features/messaging/messagingSlice";
import { markAdminConvReadApi } from "../../api/messagingApi";
import messagingBroadcast from "../../utils/messagingBroadcast";
import ConversationList from "./ConversationList";
import ChatPanel from "./ChatPanel";

const AdminMessagingHub = ({ socket }) => {
  const dispatch = useDispatch();
  const { conversations, loading, activeConversationId } = useSelector((state) => state.messaging);
  const adminId = useSelector((state) => state.adminauth?.admin?._id);
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());

  const activeConversation = conversations.find((c) => c._id === activeConversationId);

  useEffect(() => {
    dispatch(fetchAdminConversations());
  }, [dispatch]);

  useEffect(() => {
    if (!socket) return;

    const presenceHandler = ({ userId, userType, isOnline }) => {
      if (userType !== "user") return;
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        if (isOnline) next.add(userId);
        else next.delete(userId);
        return next;
      });
    };

    // Re-fetch conversations whenever socket connects/reconnects so that
    // unread counts are always up-to-date after going offline → online.
    const connectHandler = () => {
      dispatch(fetchAdminConversations());
    };

    socket.on("participant_status", presenceHandler);
    socket.on("connect", connectHandler);
    return () => {
      socket.off("participant_status", presenceHandler);
      socket.off("connect", connectHandler);
    };
  }, [socket, dispatch]);

  if (loading && conversations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 120px)">
        <CircularProgress sx={{ color: "#368AD0" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 120px)",
        bgcolor: "#FFFFFF",
        borderRadius: "20px",
        overflow: "hidden",
        border: "1px solid rgba(15,23,42,0.06)",
        boxShadow:
          "0 20px 50px rgba(15,23,42,0.06), 0 4px 12px rgba(15,23,42,0.04), inset 0 1px 0 rgba(255,255,255,0.6)",
      }}
    >
      <ConversationList
        activeId={activeConversationId}
        onSelect={(conv) => {
          dispatch(setActiveConversation(conv._id));
          dispatch(clearUnreadForConversation(conv._id));
          markAdminConvReadApi(conv._id).catch(() => {});
          messagingBroadcast.postMessage({ type: "conv_read", conversationId: conv._id });
        }}
        onlineUserIds={onlineUserIds}
      />

      <Box flex={1} display="flex" flexDirection="column" overflow="hidden">
        {activeConversation ? (
          <ChatPanel
            key={activeConversation._id}
            conversation={activeConversation}
            adminId={adminId}
            socket={socket}
            onClose={() => dispatch(setActiveConversation(null))}
          />
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            gap={2.5}
            px={4}
            sx={{ bgcolor: "#FAFBFF" }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  bgcolor: "#EFF6FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 24px rgba(54,138,208,0.12)",
                }}
              >
                <ForumRoundedIcon sx={{ fontSize: 56, color: "#368AD0" }} />
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: "#10B981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
                  border: "3px solid #FAFBFF",
                }}
              >
                <BoltIcon sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
            </Box>

            <Box textAlign="center">
              <Typography
                fontWeight={800}
                fontSize={22}
                color="#0F172A"
                letterSpacing="-0.5px"
                mb={0.75}
              >
                Your Inbox is Ready
              </Typography>
              <Typography
                variant="body2"
                color="#64748B"
                maxWidth={400}
                fontSize={14}
                lineHeight={1.6}
              >
                Pick a conversation from the left to start messaging — or enable DM
                on a user from their profile to start a brand new chat.
              </Typography>
            </Box>

            <Box display="flex" gap={1.25} mt={1}>
              <Chip
                icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                label="Private & Secure"
                size="small"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  bgcolor: "#FFFFFF",
                  color: "#475569",
                  border: "1px solid rgba(15,23,42,0.08)",
                  px: 0.5,
                }}
              />
              <Chip
                icon={<BoltIcon sx={{ fontSize: 14 }} />}
                label="Real-time"
                size="small"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  bgcolor: "#FFFFFF",
                  color: "#475569",
                  border: "1px solid rgba(15,23,42,0.08)",
                  px: 0.5,
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminMessagingHub;
